import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Hero } from '../components/layout/Hero';
import { ServiceCard } from '../components/layout/ServiceCard';
import { GeminiConcierge } from '../components/interactions/GeminiConcierge';
import { ProviderProfile } from '../components/providers/ProviderProfile';
import { BookingModal } from '../components/interactions/BookingModal';
import { ForProviders } from '../components/providers/ForProviders';
import { ProviderDashboard } from '../components/providers/ProviderDashboard';
import { CATEGORIES } from '../constants/serviceConstants';
import { ServicesService, ServiceStatus } from '../services/servicesService';
import { ProvidersService } from '../services/providerService';
import { BookingsService } from '../services/bookingService';
import { useAuth } from '../context/AuthContext';
import {
  Megaphone, Bot, Palette, Code, Headset, Cpu, ShoppingBag,
  GraduationCap, BadgeDollarSign, Scale, ShieldCheck, FileText,
  Briefcase, UserCircle, Menu, X, LayoutGrid, MessageSquareText,
  LogIn, Loader2, ChevronDown, LogOut, Settings
} from 'lucide-react';
import logo from '../assets/images/logo.png';

const iconMap = {
  Megaphone, Bot, Palette, Code, Headset, Cpu, ShoppingBag,
  GraduationCap, BadgeDollarSign, Scale, ShieldCheck, FileText,
  Briefcase, UserCircle
};

function HomePage() {
  const navigate = useNavigate();
  const { user, userProfile, logout } = useAuth();

  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isConciergeOpen, setIsConciergeOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [bookingService, setBookingService] = useState(null);
  const [currentView, setCurrentView] = useState('home');

  // User dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Firestore data states
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [currentProvider, setCurrentProvider] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load published services and providers
  useEffect(() => {
    loadServices();
  }, []);

  // Load provider profile when user changes
  useEffect(() => {
    const loadProvider = async () => {
      if (user) {
        const provider = await ProvidersService.getById(user.uid);
        setCurrentProvider(provider);
      } else {
        setCurrentProvider(null);
      }
    };
    loadProvider();
  }, [user]);

  const loadServices = async () => {
    setIsLoading(true);
    try {
      const publishedServices = await ServicesService.getPublished();
      setServices(publishedServices);

      const providerIds = [...new Set(publishedServices.map(s => s.providerId))];
      const providerData = {};
      await Promise.all(
        providerIds.map(async (id) => {
          const provider = await ProvidersService.getById(id);
          if (provider) providerData[id] = provider;
        })
      );
      setProviders(providerData);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredServices = useMemo(() => {
    return services.filter(service => {
      const matchesCategory = activeCategory === 'all' || service.categoryId === activeCategory;
      const matchesSearch = !searchQuery ||
        service.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [services, activeCategory, searchQuery]);

  const selectedProviderServices = useMemo(() => {
    if (!selectedProvider) return [];
    return services.filter(s => s.providerId === selectedProvider.id);
  }, [selectedProvider, services]);

  const handleBook = (service) => {
    setBookingService(service);
  };

  const handleBookingSubmit = async (data) => {
    try {
      const provider = providers[bookingService.providerId];
      await BookingsService.create({
        serviceId: bookingService.id,
        serviceTitle: bookingService.title,
        providerId: bookingService.providerId,
        providerName: provider?.name || 'Unknown',
        clientId: user?.uid || 'guest',
        clientName: user?.displayName || userProfile?.displayName || data.name,
        clientEmail: user?.email || data.email,
        mobile: data.mobile,
        urgency: data.urgency,
        date: data.date,
        scope: data.scope,
        priceStart: bookingService.priceStart,
      });
      alert(`Success! Your booking request for "${bookingService?.title}" has been sent to ${provider?.name || 'the provider'}.`);
      setBookingService(null);
    } catch (error) {
      console.error('Booking error:', error);
      alert('Failed to submit booking. Please try again.');
    }
  };

  const handleCategorySelect = (categoryId) => {
    if (currentView !== 'home') setCurrentView('home');
    setActiveCategory(categoryId);
    if (searchQuery) setSearchQuery('');
    setMobileMenuOpen(false);

    const grid = document.getElementById('services-grid');
    if (grid) {
      setTimeout(() => {
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleViewServiceFromChat = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      if (currentView !== 'home') setCurrentView('home');
      setActiveCategory(service.categoryId || 'all');
      setSearchQuery(service.title);
      setTimeout(() => {
        document.getElementById('services-grid')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleProviderClick = async (providerId) => {
    let provider = providers[providerId];
    if (!provider) {
      provider = await ProvidersService.getById(providerId);
    }
    if (provider) {
      setSelectedProvider(provider);
    }
  };

  const navigateTo = (view) => {
    setCurrentView(view);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleProviderSignup = () => {
    if (user) {
      navigateTo('dashboard');
    } else {
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsDropdownOpen(false);
      navigateTo('home');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleServiceSaved = () => {
    loadServices();
  };

  // Dashboard view
  if (currentView === 'dashboard') {
    const dashboardProvider = currentProvider || {
      id: user?.uid || 'demo-provider',
      name: user?.displayName || userProfile?.displayName || 'Provider',
      avatarUrl: user?.photoURL || 'https://i.pravatar.cc/150?u=demo',
      location: 'Your Location',
      bio: 'Your bio here',
      rating: 0,
      reviewCount: 0,
      jobsCompleted: 0,
      badges: [],
    };

    return (
      <ProviderDashboard
        provider={dashboardProvider}
        onLogout={handleLogout}
        onSwitchToBuying={() => navigateTo('home')}
        onServiceSaved={handleServiceSaved}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      {/* Navigation */}
      <nav className="bg-white/95 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-12">
              <div className="flex-shrink-0 flex items-center gap-2 cursor-pointer" onClick={() => navigateTo('home')}>
                <Link to="/" className="pt-3 flex items-center gap-3">
                  <img src={logo} alt="ParrotOffice" className="h-12 w-auto" />
                </Link>
              </div>

              <div className="hidden md:flex items-center space-x-8">
                <button onClick={() => navigateTo('home')} className={`font-medium transition-colors text-sm ${currentView === 'home' ? 'text-indigo-600' : 'text-slate-500 hover:text-indigo-600'}`}>How it Works</button>
                {user && currentProvider ? (
                  <button onClick={() => navigateTo('dashboard')} className={`font-medium transition-colors text-sm ${currentView === 'dashboard' ? 'text-indigo-600' : 'text-slate-500 hover:text-indigo-600'}`}>Provider Dashboard</button>
                ) : (
                  <button onClick={() => navigateTo('providers')} className={`font-medium transition-colors text-sm ${currentView === 'providers' ? 'text-indigo-600' : 'text-slate-500 hover:text-indigo-600'}`}>For Providers</button>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <div className="hidden md:flex items-center space-x-4">
                {user && userProfile ? (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center gap-3 px-3 py-2 rounded-full hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                        {userProfile.displayName ?
                          userProfile.displayName.charAt(0).toUpperCase() :
                          userProfile.email?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="hidden lg:block text-left">
                        <p className="text-sm font-medium text-slate-900 truncate max-w-[120px]">
                          {userProfile.displayName || 'User'}
                        </p>
                        <p className="text-xs text-slate-500 truncate max-w-[120px]">
                          {userProfile.email}
                        </p>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''
                          }`}
                      />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50">
                        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {userProfile.displayName || 'User'}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {userProfile.email}
                          </p>
                        </div>

                        <div className="py-1">
                          <button
                            onClick={() => {
                              setIsDropdownOpen(false);
                              navigateTo('dashboard');
                            }}
                            className="w-full flex items-center px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <LayoutGrid className="mr-3 h-4 w-4 text-slate-500" />
                            <span>Dashboard</span>
                          </button>

                          <Link
                            to="/app/settings"
                            onClick={() => setIsDropdownOpen(false)}
                            className="w-full flex items-center px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <Settings className="mr-3 h-4 w-4 text-slate-500" />
                            <span>Settings</span>
                          </Link>
                        </div>

                        <div className="border-t border-slate-100">
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <LogOut className="mr-3 h-4 w-4" />
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Link to="/login" className="text-slate-500 hover:text-indigo-600 font-medium transition-colors text-sm">
                      Log in
                    </Link>
                    <Link
                      to="/login"
                      className="bg-indigo-600 text-white px-5 py-2 rounded-full font-medium text-sm hover:bg-indigo-700 transition-colors shadow-sm"
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>

              <div className="flex items-center md:hidden ml-4">
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-50"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="relative z-50 lg:hidden">
            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
            <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-slate-900/10 shadow-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="bg-indigo-600 p-1.5 rounded-lg">
                    <LayoutGrid className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-bold text-lg tracking-tight text-slate-900">PARROTOFFICE</span>
                </div>
                <button onClick={() => setMobileMenuOpen(false)} className="-m-2.5 rounded-md p-2.5 text-slate-700 hover:bg-slate-50">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-8 flow-root">
                <div className="-my-6 divide-y divide-slate-100">
                  {/* User Profile Section - Mobile */}
                  {user && userProfile && (
                    <div className="py-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg">
                          {userProfile.displayName ?
                            userProfile.displayName.charAt(0).toUpperCase() :
                            userProfile.email?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {userProfile.displayName || 'User'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {userProfile.email}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <button
                          onClick={() => { navigateTo('dashboard'); setMobileMenuOpen(false); }}
                          className="-mx-3 w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-50"
                        >
                          <LayoutGrid className="h-5 w-5 text-slate-500" />
                          Dashboard
                        </button>
                        <Link
                          to="/app/settings"
                          onClick={() => setMobileMenuOpen(false)}
                          className="-mx-3 w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-50"
                        >
                          <Settings className="h-5 w-5 text-slate-500" />
                          Settings
                        </Link>
                        <button
                          onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                          className="-mx-3 w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-medium text-red-600 hover:bg-red-50"
                        >
                          <LogOut className="h-5 w-5" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 py-6">
                    <button onClick={() => navigateTo('home')} className="-mx-3 w-full text-left block rounded-lg px-3 py-2 text-base font-semibold text-slate-900 hover:bg-slate-50">How it Works</button>
                    {user && currentProvider ? (
                      <button onClick={() => { navigateTo('dashboard'); setMobileMenuOpen(false); }} className="-mx-3 w-full text-left block rounded-lg px-3 py-2 text-base font-semibold text-slate-900 hover:bg-slate-50">Provider Dashboard</button>
                    ) : (
                      <button onClick={() => navigateTo('providers')} className="-mx-3 w-full text-left block rounded-lg px-3 py-2 text-base font-semibold text-slate-900 hover:bg-slate-50">For Providers</button>
                    )}

                    {!user && (
                      <div className="pt-4 flex flex-col gap-3">
                        <Link
                          to="/login"
                          onClick={() => setMobileMenuOpen(false)}
                          className="w-full flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          <LogIn className="w-4 h-4" /> Log in
                        </Link>
                        <Link
                          to="/login"
                          onClick={() => setMobileMenuOpen(false)}
                          className="w-full rounded-lg bg-indigo-600 px-3 py-2.5 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 text-center"
                        >
                          Sign Up
                        </Link>
                      </div>
                    )}
                  </div>

                  {currentView === 'home' && (
                    <div className="py-6">
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Discover Services</h3>
                      <div className="space-y-1">
                        <button
                          onClick={() => handleCategorySelect('all')}
                          className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-3 ${activeCategory === 'all' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                          <LayoutGrid className="w-4 h-4" />
                          All Services
                        </button>
                        {CATEGORIES.map((cat) => {
                          const Icon = iconMap[cat.iconName] || LayoutGrid;
                          return (
                            <button
                              key={cat.id}
                              onClick={() => handleCategorySelect(cat.id)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeCategory === cat.id ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'
                                }`}
                            >
                              <Icon className="w-4 h-4" />
                              <span className="truncate">{cat.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {currentView === 'home' ? (
        <div className="flex flex-col lg:flex-row flex-grow max-w-[1600px] mx-auto w-full px-2 sm:px-4 lg:px-4 py-4 gap-2 animate-fade-in">

          {/* Sidebar */}
          <aside className="hidden lg:block lg:w-60 flex-shrink-0 lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-2">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 px-3">Discover</h2>
            <div className="space-y-1 pb-4">
              <button
                onClick={() => handleCategorySelect('all')}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeCategory === 'all' ? 'bg-indigo-50 text-indigo-700 shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                  }`}
              >
                All Services
              </button>
              {CATEGORIES.map((cat) => {
                const Icon = iconMap[cat.iconName] || LayoutGrid;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeCategory === cat.id ? 'bg-indigo-50 text-indigo-700 shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                  >
                    <Icon className={`w-4 h-4 ${activeCategory === cat.id ? 'opacity-100' : 'opacity-60'}`} />
                    <span className="truncate">{cat.name}</span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 flex flex-col gap-8">
            <Hero
              onSearch={setSearchQuery}
              onCategorySelect={handleCategorySelect}
              className="rounded-t-[2.5rem] shadow-sm border border-slate-100/50"
            />

            <div id="services-grid" className="scroll-mt-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">
                  {activeCategory === 'all'
                    ? (searchQuery ? `Results for "${searchQuery}"` : 'Recommended for you')
                    : CATEGORIES.find(c => c.id === activeCategory)?.name}
                </h2>
                <span className="text-slate-500 text-sm font-medium bg-slate-50 px-3 py-1 rounded-full">
                  {filteredServices.length} results
                </span>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : filteredServices.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredServices.map(service => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      provider={providers[service.providerId]}
                      onBook={handleBook}
                      onProviderClick={handleProviderClick}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-sm mb-4">
                    <LayoutGrid className="w-8 h-8 text-slate-300" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900">No services found</h3>
                  <p className="text-slate-500 mt-2">Try adjusting your filters or search query.</p>
                  <button
                    onClick={() => { setActiveCategory('all'); setSearchQuery('') }}
                    className="mt-6 px-6 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </main>
        </div>
      ) : (
        <ForProviders onStartSelling={handleProviderSignup} />
      )}

      {/* Floating AI Button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-4">
        {!isConciergeOpen && (
          <button
            onClick={() => setIsConciergeOpen(true)}
            className="group flex items-center gap-3 bg-slate-900 hover:bg-indigo-700 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-105"
          >
            <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-500 whitespace-nowrap opacity-0 group-hover:opacity-100 font-medium pl-1">
              AI Concierge
            </span>
            <MessageSquareText className="w-6 h-6" />
          </button>
        )}
      </div>

      <GeminiConcierge
        isOpen={isConciergeOpen}
        onClose={() => setIsConciergeOpen(false)}
        onViewService={handleViewServiceFromChat}
        services={services}
      />

      {selectedProvider && (
        <ProviderProfile
          provider={selectedProvider}
          providerServices={selectedProviderServices}
          onClose={() => setSelectedProvider(null)}
          onBookService={handleBook}
        />
      )}

      {bookingService && (
        <BookingModal
          service={bookingService}
          provider={providers[bookingService.providerId]}
          isOpen={true}
          onClose={() => setBookingService(null)}
          onSubmit={handleBookingSubmit}
          currentUser={user}
          currentProfile={userProfile}
        />
      )}
    </div>
  );
}

export default HomePage;